﻿import React from "react";
import styled from "styled-components";
import { GREY, HIGHLIGHT } from "../Colors";
import {
    IButtonColumn,
    ICheckColumn,
    IColumn,
    IMenuColumn,
    IValueColumn,
} from "./Table";
import { TableBodyCellContentButton } from "./TableBodyCellContentButton";
import { TableBodyCellContentMenu } from "./TableBodyCellContentMenu";
import { TableBodyCellContentCheck } from "./TableBodyCellContentCheck";
import { isString } from "../../Utils";

const BodyCell = styled.div`
    border-bottom: 1px solid ${GREY};
    background-color: ${(props) =>
        props.theme.selected
            ? HIGHLIGHT
            : props.theme.hover
            ? HIGHLIGHT + "22"
            : "unset"};
`;

const CellContent = styled.div`
    display: flex;
    align-items: center;
    margin: 2px 0 0 8px;
    height: calc(100% - 4px);
    width: ${(props) => props.theme.width};
    overflow: hidden;
`;

interface ITableBodyCellProps {
    style: React.CSSProperties;
    column: IColumn;
    row: any;
    selected: boolean;
    hover: boolean;
    onClick: () => void;
    onHover: () => void;
}

export const TableBodyCell: React.FunctionComponent<ITableBodyCellProps> = (
    props
) => {
    const getValueCellContent = (
        row: any,
        column: IValueColumn
    ): React.ReactNode => {
        const value = isString(column.selector)
            ? row[column.selector]
            : column.selector(row);
        return (
            <CellContent theme={{ width: column.width }}> {value}</CellContent>
        );
    };

    const getMenuCellContent = (
        row: any,
        column: IMenuColumn
    ): React.ReactNode => {
        return <TableBodyCellContentMenu row={row} column={column} />;
    };

    const getButtonCellContent = (
        row: any,
        column: IButtonColumn
    ): React.ReactNode => {
        return <TableBodyCellContentButton row={row} column={column} />;
    };

    const getCheckCellContent = (
        row: any,
        column: ICheckColumn
    ): React.ReactNode => {
        const isSelected: boolean = isString(column.selector)
            ? row[column.selector]
            : column.selector(row);
        return (
            <TableBodyCellContentCheck
                selected={isSelected}
                onClick={() => column.onClick(row)}
            />
        );
    };

    const getCellContent = (row: any, column: IColumn): React.ReactNode => {
        switch (props.column.type) {
            case "value":
                return getValueCellContent(row, column as IValueColumn);
            case "menu":
                return getMenuCellContent(row, column as IMenuColumn);
            case "button":
                return getButtonCellContent(row, column as IButtonColumn);
            case "check":
                return getCheckCellContent(row, column as ICheckColumn);
            default:
                return "";
        }
    };

    return (
        <BodyCell
            style={props.style}
            theme={{ selected: props.selected, hover: props.hover }}
            onClick={props.onClick}
            onMouseEnter={props.onHover}
        >
            {getCellContent(props.row, props.column)}
        </BodyCell>
    );
};
